import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "../hooks/useTranslation";
import { PersianTextRenderer } from "../utils/persianTextRenderer";
import SuccessToast from "./SuccessToast";
import ErrorToast from "./ErrorToast";
import UploadProgress from "./UploadProgress";
import CameraView from "./story/CameraView";
import TextStoryView from "./story/TextStoryView";
import GalleryView from "./story/GalleryView";
import StoryControls, { type StoryMode } from "./story/StoryControls";
import ImageEditor from "./story/ImageEditor";
import { useStoryCanvas } from "./story/useStoryCanvas";
import { useStoryUpload } from "./story/useStoryUpload";
import StoryCanvas from "./story/StoryCanvas";
import StoryCanvasControls from "./story/StoryCanvasControls";
import { exportCanvasToBlob } from "./story/storyCanvasExport";

type CreateStoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type CameraMode = "photo" | "video";
type FacingMode = "user" | "environment";
type EditMode = "none" | "image";

const CreateStoryModal: React.FC<CreateStoryModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<StoryMode>('canvas');
  const [cameraMode, setCameraMode] = useState<CameraMode>('photo');
  const [facingMode, setFacingMode] = useState<FacingMode>('user');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [capturedMedia, setCapturedMedia] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<'photo' | 'video' | null>(null);
  const [content, setContent] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [textBackground, setTextBackground] = useState("bg-gradient-to-br from-purple-500 to-pink-500");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [editMode, setEditMode] = useState<EditMode>("none");
  const [editedImageUrl, setEditedImageUrl] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasImageInputRef = useRef<HTMLInputElement>(null);

  const upload = useStoryUpload();
  const { token, isAuthenticated, uploading, uploadProgress, uploadStartTime, uploadFormData, resetUpload, startUpload, setUploading } = upload;
  const storyCanvas = useStoryCanvas();

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Camera handlers
  const handleCameraInit = useCallback((stream: MediaStream) => {
    streamRef.current = stream;
  }, []);

  const handleCameraStop = useCallback(() => {
    streamRef.current = null;
  }, []);

  const handleSwitchCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  }, []);

  const handleTakePhoto = useCallback(() => {
    if (!streamRef.current || !canvasRef.current) return;

    // Find the video element from CameraView
    const video = document.querySelector('video');
    if (!video || video.readyState < 2) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    if (facingMode === 'user') {
      context.scale(-1, 1);
      context.drawImage(video, -canvas.width, 0);
    } else {
      context.drawImage(video, 0, 0);
    }

    const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedMedia(photoDataUrl);
    setMediaType('photo');
    setSelectedFile(null);
    setEditMode('image'); // Open image editor
  }, [facingMode]);

  const handleStartRecording = useCallback(() => {
    if (!streamRef.current) return;

    const mediaRecorder = new MediaRecorder(streamRef.current);
    const chunks: BlobPart[] = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      const videoUrl = URL.createObjectURL(blob);
      setCapturedMedia(videoUrl);
      setMediaType('video');
      setSelectedFile(null);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setIsRecording(true);
    setRecordingTime(0);

    recordingIntervalRef.current = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= 30) {
          handleStopRecording();
          return 30;
        }
        return prev + 1;
      });
    }, 1000);
  }, []);

  const handleStopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  }, [isRecording]);

  // Gallery handlers
  const handleGallerySelect = () => {
    fileInputRef.current?.click();
  };

  const handleCanvasAddImage = () => {
    canvasImageInputRef.current?.click();
  };

  const handleCanvasImageFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file || !file.type.startsWith("image/")) return;
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrorMessage(t('errors.storyImageSize'));
        setShowError(true);
        return;
      }
      const src = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        storyCanvas.addImage(src, img.naturalWidth, img.naturalHeight);
      };
      img.onerror = () => {
        setErrorMessage(t('errors.storyUploadFailed'));
        setShowError(true);
      };
      img.src = src;
    },
    [storyCanvas]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = file.type.startsWith('video/') ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrorMessage(
        file.type.startsWith('video/') ? t('errors.storyVideoSize') : t('errors.storyImageSize')
      );
      setShowError(true);
      return;
    }

    const fileUrl = URL.createObjectURL(file);
    setCapturedMedia(fileUrl);
    setMediaType(file.type.startsWith('video/') ? 'video' : 'photo');
    setSelectedFile(file);
    
    // For photos, open image editor
    if (file.type.startsWith('image/')) {
      setEditMode('image');
    }
  };

  // Image editor handlers
  const handleImageEditSave = useCallback((editedImage: string) => {
    setEditedImageUrl(editedImage);
    setCapturedMedia(editedImage);
    setEditMode('none');
  }, []);

  const handleImageEditCancel = useCallback(() => {
    setEditMode('none');
    setEditedImageUrl(null);
  }, []);

  // Create text story canvas
  const createTextStoryCanvas = useCallback(async (): Promise<Blob | null> => {
    try {
      const renderer = new PersianTextRenderer(400, 600);
      const blob = await renderer.renderText(content, {
        backgroundColor: textBackground,
        color: '#ffffff',
        textAlign: 'center',
        padding: 40
      });
      return blob;
    } catch (error) {
      console.error('Error creating text story:', error);
      return null;
    }
  }, [content, textBackground]);

  // Upload story
  const handleUpload = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setErrorMessage(t('errors.signInRequired'));
      setShowError(true);
      return;
    }

    if (mode === 'text' && !content.trim()) {
      setErrorMessage(t('errors.storyTextRequired'));
      setShowError(true);
      return;
    }

    if ((mode === "camera" || mode === "gallery") && !capturedMedia) {
      setErrorMessage(t('errors.storyMediaRequired'));
      setShowError(true);
      return;
    }

    startUpload();

    try {
      const formData = new FormData();

      if (mode === "canvas") {
        const blob = await exportCanvasToBlob(storyCanvas.state);
        formData.append("file", blob, "story-canvas.png");
        await uploadFormData(formData);
        return;
      }

      if (mode === "text") {
        const blob = await createTextStoryCanvas();
        if (blob) {
          formData.append("file", blob, "text-story.png");
          formData.append("content", content);
          await uploadFormData(formData);
        } else {
          throw new Error(t('errors.storyCreateImageFailed'));
        }
      } else {
        if (capturedMedia) {
          if (mediaType === "photo") {
            const response = await fetch(capturedMedia);
            const blob = await response.blob();
            formData.append("file", blob, "story-photo.jpg");
          } else {
            const response = await fetch(capturedMedia);
            const blob = await response.blob();
            formData.append("file", blob, "story-video.webm");
          }
          await uploadFormData(formData);
        }
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('errors.storyCreateFailed'));
      setShowError(true);
      setUploading(false);
      resetUpload();
    }
  }, [mode, content, capturedMedia, mediaType, textBackground, token, isAuthenticated, createTextStoryCanvas, storyCanvas.state, startUpload, uploadFormData, setUploading, resetUpload]);

  // When upload completes (progress 100%, uploading false), show success toast and close after 2s.
  useEffect(() => {
    if (!uploading && uploadProgress === 100 && uploadStartTime !== null) {
      setSuccessMessage("استوری با موفقیت ایجاد شد");
      setShowSuccess(true);
      resetUpload();
      const t = setTimeout(() => handleClose(), 2000);
      return () => clearTimeout(t);
    }
  }, [uploading, uploadProgress, uploadStartTime, resetUpload]);

  const handleClose = () => {
    if (!uploading) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      setCapturedMedia(null);
      setMediaType(null);
      setContent("");
      setMode("camera");
      setCameraMode("photo");
      setFacingMode("user");
      setIsRecording(false);
      setRecordingTime(0);
      resetUpload();
      setSelectedFile(null);
      setEditMode("none");
      setEditedImageUrl(null);
      if (mode === "canvas") {
        storyCanvas.resetCanvas();
      }
      onClose();
    }
  };

  // Check authentication when modal opens
  useEffect(() => {
    if (isOpen && !isAuthenticated) {
      setErrorMessage(t('errors.signInRequired'));
      setShowError(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    }
  }, [isOpen, isAuthenticated, onClose]);

  if (!isOpen) return null;

  // Show image editor if editing image
  if (editMode === 'image' && capturedMedia && mediaType === 'photo') {
    return (
      <>
        <ErrorToast
          message={errorMessage}
          isVisible={showError}
          onClose={() => setShowError(false)}
        />
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <ImageEditor
            imageUrl={capturedMedia}
            onSave={handleImageEditSave}
            onCancel={handleImageEditCancel}
            aspectRatio={9 / 16}
          />
        </div>
      </>
    );
  }


  return (
    <>
      <SuccessToast
        message={successMessage}
        isVisible={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
      <ErrorToast
        message={errorMessage}
        isVisible={showError}
        onClose={() => setShowError(false)}
      />

      <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="relative w-full h-full max-w-md mx-auto bg-black overflow-hidden">
          {/* Controls */}
          <StoryControls
            mode={mode}
            onModeChange={setMode}
            onClose={handleClose}
            onUpload={handleUpload}
            uploading={uploading}
            disabled={!isAuthenticated}
          />

          {/* Content Area - smooth mode transitions (Instagram-like) */}
          <div className="w-full h-full pt-16 overflow-hidden">
            <AnimatePresence mode="wait">
              {mode === "camera" && !capturedMedia && (
                <motion.div
                  key="camera"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-0 w-full h-full pt-16 -mt-16"
                >
                  <CameraView
                cameraMode={cameraMode}
                facingMode={facingMode}
                isRecording={isRecording}
                recordingTime={recordingTime}
                onTakePhoto={handleTakePhoto}
                onStartRecording={handleStartRecording}
                onStopRecording={handleStopRecording}
                onSwitchCamera={handleSwitchCamera}
                onToggleMode={() => setCameraMode(cameraMode === 'photo' ? 'video' : 'photo')}
                onGallerySelect={handleGallerySelect}
                onCameraInit={handleCameraInit}
                onCameraStop={handleCameraStop}
              />
                </motion.div>
              )}

              {mode === "text" && (
                <motion.div
                  key="text"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-0 w-full h-full pt-16 -mt-16"
                >
                  <TextStoryView
                content={content}
                textBackground={textBackground}
                onContentChange={setContent}
                onBackgroundChange={setTextBackground}
              />
                </motion.div>
              )}

              {mode === "gallery" && (
                <motion.div
                  key="gallery"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-0 w-full h-full pt-16 -mt-16"
                >
                  <GalleryView
                    capturedMedia={capturedMedia}
                    mediaType={mediaType}
                    selectedFile={selectedFile}
                    onSelect={handleGallerySelect}
                    formatFileSize={formatFileSize}
                  />
                </motion.div>
              )}

              {mode === "canvas" && (
                <motion.div
                  key="canvas"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-0 w-full h-full pt-16 -mt-16 flex flex-col"
                >
                  <div className="flex-1 min-h-0 flex items-center justify-center relative">
                  {storyCanvas.state.images.length === 0 && (
                    <p className="absolute inset-0 flex items-center justify-center text-white/40 text-sm pointer-events-none z-10">
                      برای شروع تصویر اضافه کنید
                    </p>
                  )}
                  <StoryCanvas
                    state={storyCanvas.state}
                    dimensions={storyCanvas.dimensions}
                    onCanvasClick={() => {}}
                    onSelectElement={storyCanvas.selectElement}
                    onStartImageDrag={storyCanvas.startImageDrag}
                    onMoveImageDrag={storyCanvas.moveImageDrag}
                    onEndImageDrag={storyCanvas.endImageDrag}
                    onStartPinch={storyCanvas.startPinch}
                    onMovePinch={storyCanvas.movePinch}
                    onEndPinch={storyCanvas.endPinch}
                    onStartTextDrag={storyCanvas.startTextDrag}
                    onMoveTextDrag={storyCanvas.moveTextDrag}
                    onEndTextDrag={storyCanvas.endTextDrag}
                    onStartTextResize={storyCanvas.startTextResize}
                    onMoveTextResize={storyCanvas.moveTextResize}
                    onEndTextResize={storyCanvas.endTextResize}
                    onUpdateTextBlock={storyCanvas.updateTextBlock}
                    onDeleteTextBlock={storyCanvas.deleteTextBlock}
                    onDeleteImage={storyCanvas.deleteImage}
                  />
                </div>
                <StoryCanvasControls
                  onAddText={storyCanvas.addTextBlock}
                  onAddImage={handleCanvasAddImage}
                  onDeleteSelected={storyCanvas.deleteSelected}
                  hasSelection={
                    !!(
                      storyCanvas.state.selectedElementId &&
                      (storyCanvas.state.selectedElementType === 'text' ||
                        storyCanvas.state.selectedElementType === 'image')
                    )
                  }
                  selectedTextBlock={
                    storyCanvas.state.selectedElementType === 'text' && storyCanvas.state.selectedElementId
                      ? storyCanvas.state.textBlocks.find((t) => t.id === storyCanvas.state.selectedElementId) ?? null
                      : null
                  }
                  onUpdateTextBlock={storyCanvas.updateTextBlock}
                />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Preview captured media (camera/gallery) - outside AnimatePresence */}
            {(mode === 'camera' || mode === 'gallery') && capturedMedia && (
              <div className="relative w-full h-full">
                {mediaType === 'video' ? (
                  <video
                    src={capturedMedia}
                    className="w-full h-full object-contain"
                    controls
                    muted
                    playsInline
                  />
                ) : (
                  <img
                    src={capturedMedia}
                    alt="Story"
                    className="w-full h-full object-contain"
                  />
                )}

                {/* Edit buttons for photos */}
                {mediaType === 'photo' && !editMode && (
                  <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex gap-3">
                    <button
                      onClick={() => setEditMode('image')}
                      className="px-4 py-2 bg-white bg-opacity-20 backdrop-blur-md text-white rounded-full text-sm font-medium"
                    >
                      ویرایش تصویر
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>


          {/* Upload Progress */}
          {uploading && (
            <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center z-30">
              <UploadProgress
                progress={uploadProgress}
                startTime={uploadStartTime}
                message={
                  mode === 'canvas' ? "در حال ایجاد استوری..." :
                  mode === 'text' ? "در حال ایجاد استوری متنی..." :
                  mediaType === 'video' ? "در حال آپلود ویدیو..." :
                  "در حال آپلود تصویر..."
                }
              />
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <input
            ref={canvasImageInputRef}
            type="file"
            accept="image/*"
            onChange={handleCanvasImageFile}
            className="hidden"
          />

          {/* Hidden canvas for photo capture */}
          <canvas ref={canvasRef} className="hidden" />
        </div>
      </div>
    </>
  );
};

export default CreateStoryModal;
